import React, { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { TFunction } from 'i18next';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { reportError } from '../../lib/sentry';
import { GENERAL_FONT, MHColours } from '../../constants/theme';
import FlashSnackbar, { Severity } from '../common/FlashSnackbar';
import { hasStatus, serverMessage } from '../../lib/services/httpError';
import {
  ExportSource,
  exportNewVisitFormsBatch,
} from '../../lib/services/formsExportService';

const BATCH_SIZE = 100;

type RunStatus = 'running' | 'done' | 'stopped' | 'failed';

interface ExportTotals {
  status: RunStatus;
  dryRun: boolean;
  batches: number;
  fetched: number;
  published: number;
  skippedDuplicates: number;
  skippedNotSubmitted: number;
  failed: number;
  droppedFields: string[];
}

const emptyTotals = (dryRun: boolean): ExportTotals => ({
  status: 'running',
  dryRun,
  batches: 0,
  fetched: 0,
  published: 0,
  skippedDuplicates: 0,
  skippedNotSubmitted: 0,
  failed: 0,
  droppedFields: [],
});

const oneYearAgoIso = () => dayjs().subtract(1, 'year').format('YYYY-MM-DD');

const errorMessage = (err: unknown, t: TFunction) => {
  if (hasStatus(err, 409)) return t('formsExport.alreadyRunning');
  if (hasStatus(err, 501)) return t('formsExport.notConfigured');
  if (hasStatus(err, 503)) return t('formsExport.lockUnavailable');
  return serverMessage(err) ?? t('formsExport.error');
};

interface ResumePoint {
  cursor: string;
  live: boolean;
  totals: ExportTotals;
}

const FormsExportManager: React.FC = () => {
  const { t } = useTranslation();

  const [source, setSource] = useState<ExportSource>('api-service');
  const [since, setSince] = useState(oneYearAgoIso());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [totals, setTotals] = useState<ExportTotals | null>(null);
  const [resumeFrom, setResumeFrom] = useState<ResumePoint | null>(null);
  const [flash, setFlash] = useState<{
    open: boolean;
    msg: string;
    severity: Severity;
  }>({ open: false, msg: '', severity: 'success' });

  const stopRequested = useRef(false);
  const running = totals?.status === 'running';
  const missingSince = source === 'legacy-forms' && !since;

  useEffect(
    () => () => {
      stopRequested.current = true;
    },
    []
  );

  const run = async (live: boolean, from?: ResumePoint) => {
    stopRequested.current = false;
    setResumeFrom(null);
    let current: ExportTotals = {
      ...(from?.totals ?? emptyTotals(!live)),
      status: 'running',
    };
    setTotals(current);

    let cursor = from?.cursor ?? null;
    try {
      do {
        const { data } = await exportNewVisitFormsBatch({
          source,
          limit: BATCH_SIZE,
          cursor,
          ...(source === 'legacy-forms' ? { since } : {}),
          ...(live ? { confirm: 'RUN' as const } : {}),
        });
        const batch = data.result;
        current = {
          ...current,
          batches: current.batches + 1,
          fetched: current.fetched + batch.fetched,
          published: current.published + batch.published,
          skippedDuplicates:
            current.skippedDuplicates + batch.skippedDuplicates,
          skippedNotSubmitted:
            current.skippedNotSubmitted + batch.skippedNotSubmitted,
          failed: current.failed + batch.failed,
          droppedFields: Array.from(
            new Set(current.droppedFields.concat(batch.droppedFields))
          ).sort(),
        };
        setTotals(current);
        cursor = batch.nextCursor;
      } while (cursor && !stopRequested.current);

      if (cursor) {
        setTotals({ ...current, status: 'stopped' });
        setResumeFrom({ cursor, live, totals: current });
        return;
      }
      setTotals({ ...current, status: 'done' });
    } catch (err) {
      // 501 means the export is not set up in this environment, which the page explains itself.
      if (!hasStatus(err, 501)) reportError(err);
      setTotals({ ...current, status: 'failed' });
      if (cursor) setResumeFrom({ cursor, live, totals: current });
      setFlash({
        open: true,
        msg: errorMessage(err, t),
        severity: hasStatus(err, 409) ? 'warning' : 'error',
      });
    }
  };

  const changeSource = (value: ExportSource) => {
    setSource(value);
    setResumeFrom(null);
  };

  const changeSince = (value: string) => {
    setSince(value);
    setResumeFrom(null);
  };

  const countRow = (labelKey: string, value: number) => (
    <TableRow>
      <TableCell>{t(labelKey)}</TableCell>
      <TableCell align="right">{value}</TableCell>
    </TableRow>
  );

  return (
    <Box sx={{ maxWidth: 900 }}>
      <Typography
        style={{ fontSize: 20, fontWeight: 'bold', fontFamily: GENERAL_FONT }}
      >
        {t('formsExport.title')}
      </Typography>
      <Typography
        style={{
          fontFamily: GENERAL_FONT,
          fontSize: 13,
          color: MHColours.mhGrayDark,
          margin: '8px 0 16px',
        }}
      >
        {t('formsExport.intro')}
      </Typography>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              select
              size="small"
              label={t('formsExport.sourceLabel')}
              value={source}
              onChange={(e) => changeSource(e.target.value as ExportSource)}
              disabled={running}
              sx={{ width: 260 }}
            >
              <MenuItem value="api-service">
                {t('formsExport.source.apiService')}
              </MenuItem>
              <MenuItem value="legacy-forms">
                {t('formsExport.source.legacyForms')}
              </MenuItem>
            </TextField>
            {source === 'legacy-forms' && (
              <TextField
                size="small"
                type="date"
                label={t('formsExport.since')}
                value={since}
                onChange={(e) => changeSince(e.target.value)}
                InputLabelProps={{ shrink: true }}
                disabled={running}
                sx={{ width: 190 }}
              />
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button
              variant="outlined"
              disabled={running || missingSince}
              onClick={() => run(false)}
              sx={{ textTransform: 'none' }}
            >
              {t('formsExport.preview')}
            </Button>
            <Button
              variant="contained"
              disabled={running || missingSince}
              onClick={() => setConfirmOpen(true)}
              sx={{
                textTransform: 'none',
                backgroundColor: MHColours.mhGreen,
                '&:hover': { backgroundColor: MHColours.mhGreenDark },
              }}
            >
              {t('formsExport.run')}
            </Button>
            {resumeFrom && !running && (
              <Button
                variant="outlined"
                onClick={() => run(resumeFrom.live, resumeFrom)}
                sx={{ textTransform: 'none' }}
              >
                {t('formsExport.continue')}
              </Button>
            )}
            {running && (
              <Button
                onClick={() => {
                  stopRequested.current = true;
                }}
                sx={{ textTransform: 'none' }}
              >
                {t('formsExport.stop')}
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {totals && (
        <Card variant="outlined">
          <CardContent>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Typography
                style={{ fontFamily: GENERAL_FONT, fontWeight: 'bold' }}
              >
                {t(`formsExport.status.${totals.status}`)}
              </Typography>
              {totals.dryRun && (
                <Chip
                  size="small"
                  color="info"
                  label={t('formsExport.dryRun')}
                />
              )}
            </Box>

            {running && <LinearProgress sx={{ mt: 2 }} />}

            <Table size="small" sx={{ mt: 2, maxWidth: 420 }}>
              <TableBody>
                {countRow('formsExport.count.batches', totals.batches)}
                {countRow('formsExport.count.fetched', totals.fetched)}
                {countRow('formsExport.count.published', totals.published)}
                {countRow(
                  'formsExport.count.skippedDuplicates',
                  totals.skippedDuplicates
                )}
                {countRow(
                  'formsExport.count.skippedNotSubmitted',
                  totals.skippedNotSubmitted
                )}
                {countRow('formsExport.count.failed', totals.failed)}
              </TableBody>
            </Table>

            {totals.droppedFields.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography
                  style={{
                    fontFamily: GENERAL_FONT,
                    fontSize: 13,
                    marginBottom: 8,
                  }}
                >
                  {t('formsExport.droppedFields')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {totals.droppedFields.map((field) => (
                    <Chip key={field} size="small" label={field} />
                  ))}
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>{t('formsExport.confirmTitle')}</DialogTitle>
        <DialogContent>{t('formsExport.confirm')}</DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>{t('cancel')}</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              setConfirmOpen(false);
              void run(true);
            }}
          >
            {t('formsExport.run')}
          </Button>
        </DialogActions>
      </Dialog>

      <FlashSnackbar
        open={flash.open}
        msg={flash.msg}
        severity={flash.severity}
        onClose={() => setFlash((f) => ({ ...f, open: false }))}
      />
    </Box>
  );
};

export default FormsExportManager;
